import React from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { ObjectSchema } from 'yup';

import { AxiosError } from 'axios';

import API from 'utils/api';
import { isCountryCodeOnly } from 'utils/phone';
import { useStore } from 'utils/hooks/store';
import { ConfirmVerificationCodeSchema, VerifyPhoneNumberSchema, normalizePhoneNumber } from 'containers/Login/schema';
import PhoneInput from 'ui/PhoneInput';
import { apiErrors, formErrors, isBadRequest } from 'utils/api/errors';

import Logger from 'utils/logger';

import { TwoFactorAuthSetupFormProps, TwoFactorAuthSetupFormValues, TwoFactorAuthSetupStep } from './types';
import {
  ConfirmVerificationCodeContainer,
  DigitsContainer,
  FormRow,
  FormError,
  RowLabel,
  SubmitButton,
  FormInfo,
  FormStatusAnchor,
  RecoveryCodesActions,
  RecoveryCodesContainer,
  RecoveryCodesDescription,
  RecoveryCodesGrid,
  RecoveryCode,
} from './styles';
import DigitField from './components/DigitField';
import FormStatus from './components/FormStatus';

const TwoFactorAuthSetupForm: React.FunctionComponent<TwoFactorAuthSetupFormProps> = ({
  onClose,
  onInitialVerificationCodeSent,
  onVerificationCodeResent,
  onRecoveryCodesShown,
}) => {
  const initialValues: TwoFactorAuthSetupFormValues = {
    phoneNumber: '',
    verificationCode: '',
  };
  const { session, notification } = useStore();

  const [elapsedTime, setElapsedTime] = React.useState(30);

  const [step, setStep] = React.useState(TwoFactorAuthSetupStep.VerifyPhoneNumber);
  const [hasConfirmationCodeBeenSubmitted, setHasConfirmationCodeBeenSubmitted] = React.useState(false);
  const [recoveryCodes, setRecoveryCodes] = React.useState<string[]>([]);
  const [hasDownloadedCodes, setHasDownloadedCodes] = React.useState(false);

  const downloadCodes = (): void => {
    const blob = new Blob([recoveryCodes.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vatix-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
    setHasDownloadedCodes(true);
  };

  const confirmVerificationCodesInputRefs = React.useRef<Array<HTMLInputElement | null>>(
    Array.from({ length: 6 }, () => null)
  );

  const verifyPhoneNumber = async (
    { phoneNumber }: TwoFactorAuthSetupFormValues,
    onPhoneNumberVerified: () => void
  ): Promise<void> => {
    await API.verifyPhoneNumber({ phoneNumber: normalizePhoneNumber(phoneNumber) })();
    onPhoneNumberVerified();
    setStep(TwoFactorAuthSetupStep.ConfirmVerificationCode);
  };

  const confirmVerificationCode = async ({
    phoneNumber,
    verificationCode,
  }: TwoFactorAuthSetupFormValues): Promise<void> => {
    const response = await API.confirmVerificationCode({
      phoneNumber: normalizePhoneNumber(phoneNumber),
      verificationCode,
    })();
    session.user?.enableTwoFactorAuth();
    notification.enqueueSuccessSnackbar('Two-Factor Authentication enabled successfully');
    const codes = response.data.recoveryCodes ?? [];
    if (codes.length === 0) {
      onClose();
      return;
    }
    setRecoveryCodes(codes);
    setStep(TwoFactorAuthSetupStep.ShowRecoveryCodes);
    onRecoveryCodesShown();
  };

  const informationMessage = (
    helpers: FormikHelpers<TwoFactorAuthSetupFormValues>,
    values: TwoFactorAuthSetupFormValues
  ): void => {
    const interval = setInterval(() => {
      setElapsedTime((prevElapsedTime) => {
        const newElapsedTime = prevElapsedTime - 1;

        helpers.setStatus({
          info: <FormInfo>{`Didn’t receive a code? Try again in ${newElapsedTime} seconds`}</FormInfo>,
        });

        if (newElapsedTime <= 0) {
          clearInterval(interval);
          helpers.setStatus({
            info: (
              <FormInfo>
                Didn’t receive the code?{' '}
                <FormStatusAnchor
                  onClick={() => {
                    onResendCodeClick(values, helpers);
                  }}
                >
                  Click here
                </FormStatusAnchor>{' '}
                to resend
              </FormInfo>
            ),
          });
          return 30;
        }

        return newElapsedTime;
      });
    }, 1000);
  };

  const onSubmit = async (
    values: TwoFactorAuthSetupFormValues,
    helpers: FormikHelpers<TwoFactorAuthSetupFormValues>
  ): Promise<void> => {
    try {
      helpers.setSubmitting(true);

      if (step === TwoFactorAuthSetupStep.VerifyPhoneNumber) {
        await verifyPhoneNumber(values, () => {
          helpers.setStatus({
            info: <FormInfo>{`Didn’t receive a code? Try again in ${elapsedTime} seconds`}</FormInfo>,
          });
          informationMessage(helpers, values);
          onInitialVerificationCodeSent();
        });
      } else if (step === TwoFactorAuthSetupStep.ConfirmVerificationCode) {
        await confirmVerificationCode(values);
      } else if (step !== TwoFactorAuthSetupStep.ShowRecoveryCodes) {
        Logger.error(`Unknown step: ${step}`);
      }
    } catch (e) {
      const err = e as AxiosError;
      if (hasConfirmationCodeBeenSubmitted === false) {
        setHasConfirmationCodeBeenSubmitted(true);
      }

      if (!isBadRequest(err)) {
        Logger.error('Unable to setup 2fa', e);
      }
      const { nonFieldErrors, fieldErrors } = formErrors(err);
      helpers.setSubmitting(false);
      if (fieldErrors) {
        helpers.setErrors(fieldErrors);
        if (fieldErrors.verificationCode) {
          helpers.setStatus({
            errors: (
              <FormError>
                Invalid code entered. Please check and try again. If you’re having issues,{' '}
                <FormStatusAnchor
                  onClick={() => {
                    onResendCodeClick(values);
                  }}
                >
                  request a new code
                </FormStatusAnchor>
              </FormError>
            ),
          });
        }
      }
      nonFieldErrors &&
        helpers.setStatus({
          errors: (
            <>
              {nonFieldErrors.map((error) => (
                <FormError>{error}</FormError>
              ))}
            </>
          ),
          info: undefined,
        });
    }
  };

  const getValidationSchema = (): ObjectSchema<{ phoneNumber?: string } | { verificationCode?: string }> => {
    if (step === TwoFactorAuthSetupStep.VerifyPhoneNumber) {
      return VerifyPhoneNumberSchema;
    }
    if (step === TwoFactorAuthSetupStep.ConfirmVerificationCode) {
      return ConfirmVerificationCodeSchema;
    }
    if (step === TwoFactorAuthSetupStep.ShowRecoveryCodes) {
      return VerifyPhoneNumberSchema;
    }
    throw new Error(`Unknown step: ${step}`);
  };

  const onResendCodeClick = async (
    values: TwoFactorAuthSetupFormValues,
    helpers?: FormikHelpers<TwoFactorAuthSetupFormValues>
  ): Promise<void> => {
    const { phoneNumber } = values as TwoFactorAuthSetupFormValues;

    try {
      await API.verifyPhoneNumber({ phoneNumber: normalizePhoneNumber(phoneNumber) })();
      notification.enqueueSuccessSnackbar('Confirmation code has been sent');
      if (helpers) {
        informationMessage(helpers, values);
      }
      onVerificationCodeResent();
    } catch (e) {
      const err = e as AxiosError;
      Logger.error('Invalid verify phone number API response', err);
      const errors = apiErrors(err, 'Confirmation code cannot be sent right now');
      errors.forEach((message: string) => notification.enqueueErrorSnackbar(message));
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={getValidationSchema()}>
      {({ isSubmitting, status, errors, touched, submitForm, values, setFieldValue }) => (
        <Form id="change-password-form">
          {step === TwoFactorAuthSetupStep.VerifyPhoneNumber && (
            <>
              <FormRow hasError={Object.keys(errors).includes('phoneNumber') && Boolean(touched.phoneNumber)}>
                <RowLabel>Phone Number</RowLabel>
                <PhoneInput
                  style={{ width: '100%' }}
                  defaultCountry="gb"
                  preferredCountries={['gb', 'ie', 'us']}
                  regions={['north-america', 'europe', 'middle-east'] as unknown as [string]}
                  value={values.phoneNumber}
                  onChange={(e) => {
                    if (isCountryCodeOnly(e as string)) {
                      setFieldValue('phoneNumber', '');
                    } else {
                      setFieldValue('phoneNumber', e as string);
                    }
                  }}
                  variant="outlined"
                  disableAreaCodes
                  placeholder="e.g. +44 0123456789"
                  error={Boolean(touched.phoneNumber) && errors.phoneNumber !== undefined}
                  helperText={touched.phoneNumber ? errors.phoneNumber : ''}
                />
                <FormStatus status={status} />
                <SubmitButton
                  disabled={isSubmitting || Object.keys(errors).length > 0}
                  type="submit"
                  size="large"
                  color="primary"
                  variant="contained"
                  onClick={submitForm}
                >
                  Send Code
                </SubmitButton>
              </FormRow>
            </>
          )}
          {step === TwoFactorAuthSetupStep.ConfirmVerificationCode && (
            <ConfirmVerificationCodeContainer>
              <DigitsContainer>
                {Array.from({ length: 6 }, (_, index) => (
                  <DigitField
                    index={index}
                    inputRef={(el) => {
                      confirmVerificationCodesInputRefs.current[index] = el;
                    }}
                    onValueEntered={(digitIndex) => {
                      confirmVerificationCodesInputRefs.current[digitIndex + 1]?.focus();
                    }}
                    onBackSpacePressed={(digitIndex) => {
                      confirmVerificationCodesInputRefs.current[digitIndex - 1]?.focus();
                    }}
                    confirmationCodeSubmitted={hasConfirmationCodeBeenSubmitted}
                    data-testid={`digit-${index}`}
                  />
                ))}
              </DigitsContainer>
              <FormStatus status={status} />
              <SubmitButton
                disabled={isSubmitting || Object.keys(errors).length > 0 || values.verificationCode.length !== 6}
                type="submit"
                size="large"
                color="primary"
                variant="contained"
                onClick={() => {
                  submitForm();
                }}
              >
                Continue
              </SubmitButton>
            </ConfirmVerificationCodeContainer>
          )}
          {step === TwoFactorAuthSetupStep.ShowRecoveryCodes && (
            <RecoveryCodesContainer>
              <RecoveryCodesDescription>
                Save these recovery codes in a safe place. They can be used to access your account if you lose access to
                your phone. Each code can only be used once and they will not be shown again.
              </RecoveryCodesDescription>
              <RecoveryCodesGrid>
                {recoveryCodes.map((code) => (
                  <RecoveryCode key={code} data-testid="recovery-code">
                    {code}
                  </RecoveryCode>
                ))}
              </RecoveryCodesGrid>
              <RecoveryCodesActions>
                <SubmitButton type="button" variant="outlined" onClick={downloadCodes}>
                  Download recovery codes
                </SubmitButton>
                <SubmitButton
                  disabled={!hasDownloadedCodes}
                  type="button"
                  size="large"
                  color="primary"
                  variant="contained"
                  onClick={onClose}
                >
                  I've saved my recovery codes
                </SubmitButton>
              </RecoveryCodesActions>
            </RecoveryCodesContainer>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default TwoFactorAuthSetupForm;
